import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import BaseDataEntryDialog from "./BaseDataEntryDialog.jsx";
import ImageUploadButton from "components/common/buttons/ImageUploadButton.js";
import { submitBugReport } from "store/reducers/inputReducer";
import DialogTextField from "components/common/inputs/DialogTextField.js";
import FieldLabel from "components/common/inputs/FieldLabel.jsx";

function BugDialog(props) {
  const dispatch = useDispatch();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [bugMessage, setBugMessage] = useState("");

  const handleSubmitReport = () => {
    if (!bugMessage.trim()) return;
    dispatch(
      submitBugReport({
        user_email: email,
        bug_message: bugMessage,
        bug_files: selectedFiles,
        bug_filenames: selectedFileNames,
      })
    );
    setSubmitted(true);
  };

  const handleClose = () => {
    props.handleClose();
    // Reset state after a delay to allow the closing animation to finish
    setTimeout(() => {
      setSubmitted(false);
      setBugMessage("");
      setEmail("");
      setSelectedFiles([]);
      setSelectedFileNames([]);
    }, 300);
  };

  return (
    <BaseDataEntryDialog
      open={props.open}
      onClose={handleClose}
      title="Support & Feedback"
      description="Have a question, suggestion, or found an issue? We'd love to hear from you."
      maxWidth="sm"
      titleId="bug-dialog-title"
      descriptionId="bug-dialog-description"
      actions={
        !submitted ? (
          <>
            <Button
              onClick={handleClose}
              sx={{ color: "text.secondary", fontWeight: 600, textTransform: "none" }}
            >
              Discard
            </Button>
            <Button
              onClick={handleSubmitReport}
              variant="contained"
              disabled={!bugMessage.trim()}
              sx={{
                px: 3,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Submit
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              py: 1.25,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Got it, thanks!
          </Button>
        )
      }
    >
      {!submitted ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DialogTextField
            label="Email address (optional)"
            placeholder="your@email.com"
            type="email"
            fullWidth
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <DialogTextField
            label="Message"
            placeholder="Tell us what's on your mind..."
            multiline
            rows={5}
            fullWidth
            required
            value={bugMessage}
            onChange={(e) => setBugMessage(e.target.value)}
          />

          <Box>
            {/* No icon here — the upload button below carries one, and two icons
                for one field is the sort of duplication that reads as clutter. */}
            <FieldLabel optional sx={{ mb: 1 }}>
              Screenshots
            </FieldLabel>
            <ImageUploadButton
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              selectedFileNames={selectedFileNames}
              setSelectedFileNames={setSelectedFileNames}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            py: 3,
          }}
        >
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: "success.main", mb: 2 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: "dark.main" }}>
            Submission Received
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", maxWidth: 320, lineHeight: 1.6 }}
          >
            Thank you for helping us improve! Our engineers will review your report
            and take action as soon as possible.
          </Typography>
        </Box>
      )}
    </BaseDataEntryDialog>
  );
}

BugDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default BugDialog;
